import PortalHelper, {
  Context,
  rewriteWebpackPublicPath,
  travelTree
} from '@portal/vue-helper'
import ElementPlus from 'element-plus'
import { createApp } from 'vue'
import App from './App.vue'
import { ENTRY_KEY, MGR_APP_ID, sdk } from './portal'
import buildRouter from './router'
import { cloneDeep, omit } from 'lodash-es'

// 重写webpack的publicPath
rewriteWebpackPublicPath()

/**
 * 初始化启动参数
 * @param {import("@portal/vue-helper").AppBootArgs} args
 * @param {import("@portal/vue-helper").default} helper
 */
async function initBootArgs (token, helper) {
  // 完成SDK登录
  sdk.setAuthInfo(token)
  // 获取当前用户路由
  const routes = await helper.routes(ENTRY_KEY)
  // 获取路由映射表
  const mapping = await helper.mapping(routes)
  return {
    routes,
    mapping
  }
}

/**
 * 从基座下发的上下文数据中构造启动参数
 * @param {*} payload 基座应用下发上下文数据
 * @param {*} helper helper
 * @returns
 */
async function getBootArgs (payload, helper) {
  const profile = payload.profile

  const menus = (payload?.extra?.menus ?? [])

  const getRenderFile = (type) => {
    return ({
      viz: 'index',
      page: 'index',
      block: 'index',
      iframe: 'index',
      url: 'index'
    }[type]) ?? 'error'
  }
  const getParams = (obj) => {
    switch (obj.type) {
      case 'scene': {
        const config = Object.assign({}, obj.params.scene, {
          olapp: obj.params.scene.olapp ?? MGR_APP_ID,
          olappver: obj.params.scene.olappver ?? 'v2'
        })
        // const config = obj.params.scene
        switch (obj.params.scene.type) {
          case 'block': {
            return { type: 'block', config }
          }
          default: {
            return { type: obj.type, config }
          }
        }
      }
      case 'url':
      case 'iframe': {
        return { type: obj.type, config: obj.params.webpage }
      }
      case 'group': {
        return null
      }
    }
  }
  const formatMenus = (arr, pid = null) => {
    const results = []
    for (const item of arr) {
      const obj = cloneDeep(item)
      obj.params = getParams(obj)
      obj.url = obj.id
      obj.file = obj.type === 'group'
        ? 'default'
        : getRenderFile(obj.type === 'scene' ? obj.params.config.type : obj.type)
      obj.type = obj.type === 'group' ? 'GROUP_MENU' : 'MENU'
      obj.parent_id = pid
      delete obj.path
      results.push(obj)
      if (obj.children) {
        results.push(...formatMenus(obj.children, obj.id))
      }
    }
    return results
  }

  profile.menus = formatMenus(menus)

  // 完成SDK登录
  sdk.setAuthInfo(payload.token)
  // 设置sdk的profile缓存
  sdk.cache.set('app.profile', profile)
  // 构造当前用户路由
  const rawRoutes = await helper.toRoutes(profile.menus, ENTRY_KEY)
  const routes = travelTree(rawRoutes, (route) => {
    return {
      ...omit(route, 'params'),
      meta: route.params
    }
  })

  // 获取路由映射表
  const mapping = await helper.mapping(routes)
  return {
    routes,
    mapping,
    profile
  }
}

/**
 * 启动应用
 * @param {import("@portal/vue-helper").AppBootArgs} args
 * @param {import("@portal/vue-helper").default} helper
 */
async function bootstrap (args, helper) {
  // 构造路由实例
  const basename = globalThis.__MICRO_APP_BASE_ROUTE__ || '/'
  const router = buildRouter(args.routes, basename)

  // 将helper放入window中
  globalThis.$helper = helper

  // 将router放入window中
  globalThis.$router = router

  // 监听路由的变化
  // 将$route放入window中
  router.afterEach((to) => {
    globalThis.$route = to
  })
  // 子应用路由的变化需要通知基座应用
  router.beforeEach(helper.handler.beforeRoute)

  // 注入全局样式
  helper.injectGlobalStyle()

  // 创建Vue应用实例
  const app = createApp(App)

  // 挂载路由实例
  app.use(router)

  // 挂载helper帮助上下文
  app.use(Context, {
    sdk,
    profile: args.profile
  })

  // 挂载UI组件库
  app.use(ElementPlus)

  // 将应用挂载至DOM
  app.mount('#workbench-render-app')

  // 仅在微前端模式下执行
  if (globalThis.microApp) {
    // 实现页面导航方法
    const navigate = (to) => {
      router.push({ ...to, name: to.key })
    }

    // 监听基座应用的消息
    const removeListener = helper.handler.listenFoundationAppEvents({
      // 处理跳转页面事件
      navigate,
      other: (msg) => {
        switch (msg.type) {
          // 答复基座，最新路由状态
          case 'req:report-current-route': {
            helper.dispatcher.dispatch({
              type: 'resp:report-current-route',
              payload: globalThis.$route
            })
            break
          }
        }
      }
    }, false)

    // 自动跳转默认页
    const page = sdk.cache.get('navigate')
    if (page) {
      navigate(page)
      sdk.cache.delete('navigate')
    }

    // 监听应用卸载事件(注意：keep-alive模式下不会触发)
    globalThis.addEventListener('unmount', function () {
      // 卸载消息监听器
      removeListener?.()
    })
  }

  // 输出「已就绪」日志
  console.log('ready')
}

/**
 * 启动入口
 */
async function start () {
  // 应用是否已启动
  let started = false

  // 构造帮助实例
  const helper = new PortalHelper(sdk, {
    appName: 'portal',
    loadView: (file) => import(`./pages/${file}.vue`)
  })

  if (globalThis.microApp) {
    // 监听基座应用的消息
    const removeListener = helper.handler.listenFoundationAppEvents({
      navigate: (to) => {
        sdk.cache.set('navigate', to)
      },
      other: async (msg) => {
        switch (msg.type) {
          case 'resp:get-ctx': {
            // console.log('ctx信息为：', msg.payload)
            if (!started) {
              // 标记为已启动
              started = true
              // 收集参数
              const args = await getBootArgs(msg.payload, helper)
              // 启动应用
              await bootstrap(args, helper)
              // 移除消息监听
              removeListener?.()
            }
            break
          }
        }
      }
    }, false)
    // 获取子应用上下文
    helper.dispatcher.dispatch({
      type: 'get-ctx'
    })
  } else {
    // 从url或者基座应用获取token
    const token = helper.tokenParser.parse()

    // 如果没有token，重定向至认证中心
    if (!token) {
      helper.toLogin()
      return
    }

    // 缓存token到本地
    helper.cacheStore.set(token)

    // 移除url中的token
    helper.tokenParser.remove()

    try {
      // 启动应用
      await bootstrap(await initBootArgs(token, helper), helper)
    } catch (err) {
      // 处理token过期异常
      if (err.response && err.response.status === 401) {
      // 重定向至统一认证中心
        helper.toLogin()
      }
    }
  }
}

/**
 * 启动应用
 */
start()
